/** The source catalog. Other languages are typed by these keys; a phrase is always whole. */
export const en = {
  'app.title': 'Tellvo.app — phone setup',
  'brand.name': 'Tellvo.app',
  'theme.label': 'Page theme',
  'theme.system': 'As in the system',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  'connect.connecting': 'Connecting to the setup service…',
  'connect.waitingPhone': 'Waiting for data from the iPhone…',

  'pair.title': 'Connect your iPhone',
  'pair.lead': 'Set up your phone lines on this big screen. Everything you type shows up on your iPhone instantly.',
  'pair.qr': 'Point the iPhone camera here',
  'pair.qrLabel': 'QR code for the iPhone camera',
  'pair.codeLabel': 'Pairing code',
  'pair.privacy': 'Everything you enter is encrypted between this browser and the phone. The Tellvo.app server only sees ciphertext.',
  'pair.timer': 'The code works for another {time}',
  'pair.or': 'or type the code in Tellvo.app',
  'pair.expired.title': 'The code has expired',
  'pair.expired.text': 'It worked for 2 minutes. Get a new one and point the camera again.',
  'pair.renew': 'New code',
  'pair.lost.title': 'Connection dropped',
  'pair.lost.text': 'Reconnecting. The QR code stays the same; the code will change.',
  'pair.offline.title': 'The connection did not come back',
  'pair.offline.text': 'Check the internet on this computer and get a new code.',
  'sync.reconnecting': 'Reconnecting…',
  'sync.offline': 'No connection',
  'notice.reconnecting': 'The connection to the setup service dropped; reconnecting. You can keep going: changes will wait.',
  'notice.offline': 'The connection did not come back. Changes are kept on this page and will go out when it does.',
  'action.retry': 'Retry',

  'confirm.title': 'An iPhone connected. Allow it?',
  'confirm.text': 'Allow it only if you typed the code on this phone yourself.',
  'confirm.allow': 'Allow',
  'confirm.reject': 'Reject',

  'sync.online': 'Connected',
  'sync.away': 'In background',
  'sync.device': '{device} · Tellvo.app {version}',
  'sync.pending': 'Not synced: {count}',

  'accounts.title': 'Phone lines',
  'accounts.add': 'Add line',
  'accounts.empty': 'No accounts yet. You need the server, username and password from your provider.',
  'accounts.addFirst': 'Add a line',
  'accounts.toggle': '{name} is active',

  'state.working': 'Ready',
  'state.connecting': 'Connecting',
  'state.error': 'Error',
  'state.off': 'Off',
  'state.idle': 'Idle',
  'state.waiting': 'Waiting',

  'line.notOnPhone': 'Not on the iPhone yet',
  'line.pendingAway': 'Applies when the iPhone is back',
  'line.pendingOnline': 'Sending to the iPhone',
  'line.deleting': 'Deletes after you confirm on the iPhone',
  'line.failed': 'Not applied',
  'line.off': 'Registration is off',
  'line.error': 'Does not register',
  'line.pushMissing': "Won't ring while closed",

  'editor.new': 'New account',
  'editor.back': 'Back to accounts',
  'editor.pick': 'Pick an account on the left or add a new one.',
  'editor.advanced': 'Advanced',
  'editor.passwordKeep': 'Saved on the iPhone. Leave empty to keep it.',
  'editor.failed': 'The iPhone did not apply this account: {reason}',
  'editor.registrationFailed': '{reason} Check the server, username and password, or try another transport.',
  'editor.switchFirst': 'Save or cancel the changes first.',

  'push.off': 'Calls while the app is closed are turned off.',
  'push.transport': "With the app closed, calls won't come through: the push service works over UDP and TCP for now, and this account uses {transport}.",
  'push.afterSave': 'Goes to the push service after you save.',
  'push.noPassword': "With the app closed, calls won't come through: the iPhone has no password for this account.",
  'push.noToken': 'The iPhone has not issued a push token yet. Open Tellvo.app on the phone.',
  'push.refused': "The push service did not accept this account ({status}). With the app closed, calls won't come through.",
  'push.unreachable': 'The push service did not answer. Tellvo.app tries again on the next change.',
  'push.useUdp': 'Use UDP',
  'accounts.active': 'Active',
  'accounts.pushToggle': 'Calls when the app is closed: {name}',
  'line.pushNoRoom': 'No room for push: {used} of {limit}',
  'line.pushNoRoomBare': 'No room for push',
  'fact.dnd': 'Do Not Disturb on the iPhone',
  'push.noRoom': 'No room in your plan: push is on for {used} of {limit}.',
  'push.noRoomBare': 'No room in your plan: push is on for every line it allows.',
  'push.otherDevices': 'Other devices hold every place in your plan.',
  'push.offAll': 'Off for all lines on the iPhone.',
  'push.enable': 'Turn on',

  'field.displayName': 'Name',
  'field.displayName.hint': 'Shown in the account picker on the phone.',
  'field.domain': 'Server',
  'field.domain.hint': 'From your provider, for example sip.example.com.',
  'field.username': 'Username',
  'field.username.hint': 'Usually your number or login at the provider.',
  'field.password': 'Password',
  'field.password.hint': 'Kept only on the iPhone.',
  'field.authUsername': 'Auth username',
  'field.authUsername.hint': 'Only if the provider gives a separate one.',
  'field.registrar': 'Registrar',
  'field.registrar.hint': 'Only if the provider names one.',
  'field.outboundProxy': 'Proxy',
  'field.outboundProxy.hint': 'Only if the provider names one.',
  'field.transport': 'Transport',
  'field.transport.hint': 'How the phone talks to the server.',
  'field.port': 'Port',
  'field.port.hint': 'Empty means the standard port for the transport.',
  'field.srtp': 'Call encryption',
  'field.srtp.hint': 'Whether the sound of a call is encrypted.',
  'field.registerIntervalSeconds': 'Re-register every',
  'field.registerIntervalSeconds.hint': 'In seconds. Empty means 600.',
  'field.stunServer': 'STUN server',
  'field.stunServer.hint': 'Helps behind some routers. Empty means none.',
  'field.trustedCertificateSha256': 'Trusted certificate',
  'field.trustedCertificateSha256.hint': 'TLS only: the SHA-256 fingerprint of your PBX certificate, if the phone does not trust it on its own (self-signed). Empty means the system decides.',
  'field.trustedCertificateSha256.invalid': 'Enter 64 hex digits; colons and spaces are fine.',
  'field.codecs': 'Codecs',
  'field.codecs.hint': 'The phone offers them in this order.',

  'srtp.disabled': 'Off',
  'srtp.disabled.note': 'Sound goes unencrypted. Works with every server.',
  'srtp.optional': 'When available',
  'srtp.optional.note': 'Encrypts when the server supports it, otherwise plain.',
  'srtp.mandatory': 'Required',
  'srtp.mandatory.note': 'A call does not start without encryption.',

  'foot.missing': 'Missing: {fields}',
  'foot.away': 'Check works when the iPhone is connected.',
  'foot.password': 'The new password is confirmed on the iPhone.',

  'action.check': 'Check',
  'action.checking': 'Checking…',
  'action.save': 'Save',
  'action.delete': 'Delete',
  'action.cancel': 'Cancel',
  'action.startOver': 'Start over',
  'action.copy': 'Copy',
  'action.showLog': 'Show log',

  'check.title': 'Check',
  'check.registered': 'The account registers.',
  'check.failed': 'It does not register: {reason}',

  'notice.saved': 'Saved on the iPhone.',
  'notice.savedHere': 'Saved here. It applies when the iPhone is back.',
  'notice.deleted': 'Account deleted.',
  'notice.rejected': 'Declined on the iPhone.',
  'notice.plansOpen': 'The plans are open on your iPhone.',
  'notice.plansClosed': 'The iPhone could not show the plans. Open Tellvo.app on it and try again.',
  'notice.confirmDelete': 'Confirm the deletion on the iPhone.',
  'notice.confirmPassword': 'Confirm the new password on the iPhone.',
  'notice.deleteLater': 'The deletion applies when the iPhone is back. You confirm it on the phone.',

  'app.section': 'On the iPhone',
  'app.appearance': 'App appearance on the iPhone',
  'app.appearance.hint': 'Only the app on the phone. This page has its own switch at the top.',
  'app.appearance.system': 'As in iOS',
  'app.appearance.light': 'Light',
  'app.appearance.dark': 'Dark',

  'features.section': 'Features',
  'features.pushIncoming': 'Calls when the app is closed',
  'features.pushIncoming.hint': 'A call reaches you even if Tellvo.app is not running.',
  'features.pushIncoming.unavailable': 'Comes with the Tellvo.app subscription.',
  'features.sms': 'SMS through the provider',
  'features.sms.hint': 'Send and receive SMS with providers that support it.',
  'features.sms.unavailable': 'In development.',
  'features.soon': 'Soon',
  'features.toggle': 'Turn on {name}',

  'diag.section': 'Diagnostics',
  'diag.log': 'SIP log',
  'diag.log.hint': 'Everything the iPhone exchanged with the servers. Useful for your provider’s support.',
  'diag.empty': 'The log is empty.',

  'lost.text': 'Check the internet on this computer. Starting over means connecting the iPhone again; changes it has not received yet are lost.',

  'error.rejected': 'You declined the connection. Start over to connect again.',
  'error.decrypt': 'A message from the phone could not be read. Start over to connect again.',
  'error.bye': 'The phone ended the session.',
  'error.expired': 'The code and the QR code expired.',
  'error.roomFull': 'Another browser is already connected to this page.',
  'error.tooMany': 'Too many wrong codes.',
  'error.relay': 'The setup service refused the connection ({code}).',
  'field.invalidNumber': 'Enter a whole number from {min} to {max}.',
  'landing.text': 'This link opens Tellvo.app on the iPhone. Scan the QR code with the phone camera.',

  // Redesign C (tellvo-voip-client#96)
  'brand.page': 'Phone setup',
  'pair.step1': 'Open Tellvo.app on your iPhone',
  'pair.step2': 'Go to {path}',
  'pair.step2.path': 'Settings › Manage from computer',
  'pair.step3': 'Scan the code with the camera',
  'pair.step3.hint': 'Or type the 6 digits in Tellvo.app. Either way works.',
  'pair.get': 'Don’t have Tellvo.app yet?',
  'pair.appStore': 'Download on the App Store',
  'console.summary.empty': 'Let’s set up your first phone line',
  'console.summary.all': 'All lines are ready for calls',
  'console.summary.some': 'Lines ready: {ready} of {total}',
  'console.summary.attention': 'Lines ready: {ready} of {total}. {name} needs attention.',
  'console.sub.empty': 'Type your provider’s settings here. They appear on your iPhone as you type, and the iPhone tests them for you.',
  'console.sub.ok': 'Changes you make here show up on your iPhone as you type.',
  'console.sub.attention': 'The server didn’t accept the sign-in. Open the line to fix it; your iPhone retries as soon as you save.',
  'console.fix': 'Fix {name}',
  'console.stat.ready': 'Ready',
  'console.stat.attention': 'Need attention',
  'console.stat.quiet': 'Don’t ring when closed',
  'link.browser': 'This browser',
  'link.encrypted': 'End-to-end encrypted',
  'link.live': 'Live. Edits appear on the iPhone as you type.',
  'link.away': 'Your iPhone is in the background. Changes sync when Tellvo.app is open again.',
  'link.waiting': 'Changes waiting for the iPhone: {count}. They sync when Tellvo.app is open again.',
  'link.reconnecting': 'Lost the connection to the setup service. Reconnecting; your edits wait here.',
  'link.offline': 'The connection didn’t come back. Your edits stay here until it does.',
  'accounts.lead': 'Each line is a SIP account. Turn one off to stop its calls without deleting it.',
  'accounts.addHint': 'Server, username and password from your provider',
  'accounts.fix': 'Fix sign-in',
  'accounts.edit': 'Edit {name}',
  'accounts.drag': 'Drag to change the dialing order',
  'accounts.moveHint': 'The lines dial in this order. Drag a card to move it; from the keyboard, Alt with the arrow keys moves the focused one.',
  'accounts.moved': 'Moved to position {n} of {total}',
  'fact.off': 'Turned off, no calls',
  'fact.open': 'Rings while the app is open',
  'fact.closed': 'Rings even when the app is closed',
  'fact.enc.off': 'Calls not encrypted',
  'fact.enc.optional': 'Encrypted calls',
  'fact.enc.mandatory': 'Encrypted calls only',
  'line.ext': 'Ext. {n}',
  'start.1.title': 'Add a line',
  'start.1.text': 'Server, username and password. Your provider sends them when you sign up.',
  'start.2.title': 'Test it',
  'start.2.text': 'Your iPhone signs in to the server and shows the result here.',
  'start.3.title': 'Take calls',
  'start.3.text': 'Calls ring on your iPhone like regular ones, even on the Lock Screen.',
  'plan.trial': 'First month free',
  'plan.title': 'Let every call through, even when Tellvo.app is closed',
  'plan.lead': '{lines} ring only while the app is open. With a subscription your iPhone rings on the Lock Screen like a regular phone, and the app stays closed until you answer.',
  'plan.lead.yourLines': 'Your lines',
  'plan.billing': 'Billing period',
  'plan.monthly': 'Monthly',
  'plan.yearly': 'Yearly',
  'plan.save.months': '2 months free',
  'plan.save.percent': 'Save {n}%',
  'plan.source': 'Prices from {device} · App Store, {region}',
  'plan.pick': 'Plan',
  'plan.personal': 'Personal',
  'plan.personal.limits': '3 accounts, 2 devices',
  'plan.family': 'Family',
  'plan.family.limits': 'Up to 6 people, 3 accounts and 2 devices each',
  'plan.pro': 'Pro',
  'plan.pro.limits': '10 accounts, 5 devices',
  'plan.fits': 'Fits your accounts',
  'plan.perMonth': '/ month',
  'plan.perYear': '/ year',
  'plan.try': 'Try {plan} free for a month',
  'plan.subscribe': 'Subscribe to {plan}',
  'plan.ctaHint': 'Opens the plans on {device}. You confirm with Face ID; cancel anytime in the App Store.',
  'plan.away': 'Open Tellvo.app on your iPhone to see your prices and start the free month there.',
  'plan.how': 'Subscribe in Tellvo.app on your iPhone:',
  'plan.currency': 'Prices are in your App Store currency.',
  'plan.path': 'Settings › Calls When App Is Closed',
  'plan.shot.alt': 'An incoming call on the Lock Screen while Tellvo.app is closed',
  'plan.shot.caption': 'The app is closed. The call still rings.',
  'plan.shot.line': 'Main line · Tellvo.app',
  'plan.shot.incoming': 'incoming call',
  'editor.group.signin': 'Sign-in',
  'editor.group.security': 'Security and audio',
  'editor.group.connection': 'Advanced connection',
  'editor.group.connection.hint': 'Usually not needed',
  'editor.close': 'Close',
  'editor.new.sub': 'Settings appear on your iPhone as you type',
  'check.showLog': 'Show SIP messages',
  'features.paid': 'With a subscription',
} as const;

export type Key = keyof typeof en;
